import { useSpaceNoun } from '@/lib/useSpaceNoun';
import { ChevronDown } from 'lucide-react';
import { useEffect, useState } from 'react';

interface Run {
  id: string;
  name: string;
  sizeCategory: string;
  speciesAllowed: string;
  capacityMax?: number;
}

interface Props {
  facilityId: string;
  value: string | null;
  onChange: (id: string | null) => void;
  /** ISO 8601 datetime — when provided alongside endsAt, fetches only available runs */
  startsAt?: string | undefined;
  /** ISO 8601 datetime — when provided alongside startsAt, fetches only available runs */
  endsAt?: string | undefined;
  /** Run IDs already assigned to other pets in the same booking (excluded from options) */
  excludeRunIds?: string[] | undefined;
  /**
   * The pet's species. Only runs whose `speciesAllowed` includes it are
   * offered — the API refuses a cat in a dog-only kennel (422
   * `run_species_mismatch`), so the picker never shows one.
   */
  species?: string | undefined;
}

export function parseSpeciesAllowed(json: string): string[] {
  try {
    const parsed = JSON.parse(json) as unknown;
    return Array.isArray(parsed) ? parsed.filter((s): s is string => typeof s === 'string') : [];
  } catch {
    return [];
  }
}

export function RunPicker({
  facilityId,
  value,
  onChange,
  startsAt,
  endsAt,
  excludeRunIds,
  species,
}: Props) {
  const noun = useSpaceNoun();
  const [runs, setRuns] = useState<Run[]>([]);

  useEffect(() => {
    const useAvailable = Boolean(startsAt && endsAt);
    const speciesQs = species ? `&species=${encodeURIComponent(species)}` : '';
    const url = useAvailable
      ? `/api/runs/available?facilityId=${encodeURIComponent(facilityId)}&startsAt=${encodeURIComponent(startsAt!)}&endsAt=${encodeURIComponent(endsAt!)}${speciesQs}`
      : `/api/runs?facilityId=${encodeURIComponent(facilityId)}`;

    fetch(url, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : Promise.resolve([])))
      .then((data: unknown) => {
        if (Array.isArray(data)) setRuns(data as Run[]);
      })
      .catch(() => setRuns([]));
  }, [facilityId, startsAt, endsAt, species]);

  const inputId = `run-picker-${facilityId}`;
  // Filter out runs already claimed by sibling pets (unless capacityMax > 1),
  // and runs that don't take this species (the plain /api/runs list isn't
  // filtered server-side; the /available one is — this is belt and braces).
  const excluded = new Set(excludeRunIds ?? []);
  const availableRuns = runs.filter((r) => {
    if (species && !parseSpeciesAllowed(r.speciesAllowed).includes(species)) return false;
    if (!excluded.has(r.id)) return true;
    // Allow shared runs with capacity > 1 (e.g. cat colony rooms)
    return (r.capacityMax ?? 1) > 1;
  });

  return (
    <div>
      {availableRuns.length === 0 && runs.length > 0 && species ? (
        <p className="text-sm text-muted-foreground" data-testid={`${inputId}-none`}>
          No {noun.plural} at this site take {species}s{startsAt && endsAt ? ' on these dates' : ''}
          . Leave unassigned or change the {noun.singular}'s allowed species in Settings →{' '}
          {noun.Plural}.
        </p>
      ) : availableRuns.length === 0 ? (
        <input
          id={inputId}
          type="text"
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value || null)}
          placeholder={`${noun.Singular} name (optional)`}
          className="num h-10 w-full rounded-md border border-border bg-surface px-3 text-sm outline-none focus:ring-2 focus:ring-ring/30"
        />
      ) : (
        <div className="relative">
          <select
            id={inputId}
            value={value ?? ''}
            onChange={(e) => onChange(e.target.value || null)}
            className="num h-10 w-full appearance-none rounded-md border border-border bg-surface pl-3 pr-9 text-sm outline-none focus:ring-2 focus:ring-ring/30"
          >
            <option value="">Select a {noun.singular}</option>
            {availableRuns.map((r) => (
              <option key={r.id} value={r.id}>
                {r.name}
              </option>
            ))}
          </select>
          <ChevronDown
            className="pointer-events-none absolute right-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
            strokeWidth={1.75}
          />
        </div>
      )}
    </div>
  );
}
